import { Debug } from '@svt/debug';
import { SlashTiming } from '@ui-components/design-tokens';
import { COOKIE_CONSENT_VERSION } from '@utils/Cookies';
import { loadPersistentActions } from '@utils/persistentState';
import { reportWebVitalsToHelix } from '@utils/statistics/performanceUtils';
import { fetchParentalLock } from 'app/actions/parentalLock';
import ExternalScripts from 'app/components/App/ExternalScripts';
import CookieConsentContainer from 'app/components/CookieConsent/CookieConsentContainer';
import { CookieConsentContextProvider } from 'app/components/CookieConsent/CookieConsentProvider';
import VideoModal from 'app/components/VideoModal/VideoModal';
import { UserContextProvider } from 'app/context/UserContext';
import { usePrevious } from 'app/hooks/usePreviousHook';
import useReloadManager from 'app/hooks/useReloadManager';
import { fromJS } from 'immutable';
import setupAbiskoServer from 'lib/abiskoServer';
import { withUrqlSsr } from 'lib/urql';
import { NextUrqlAppContext, NextUrqlClientConfig, withUrqlClient } from 'next-urql';
import NextApp, { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import BaseHtmlHeader from '../app/components/App/BaseHtmlHeader';
import BaseLayout from '../app/components/App/BaseLayout';
import '../app/components/App/normalize.css';
import { createServerStore, useClientStore } from '../lib/redux';

const { error: errorLogger } = Debug('play:client:app:error');
interface Props extends AppProps {
    initialStore: object;
    reduxStore: Store<any, any>;
}

const DelayedUnmount = ({ shouldDelay, children }) => {
    const [displayChildren, setDisplayChildren] = useState(children);

    useEffect(() => {
        let timeout;

        if (shouldDelay) {
            timeout = setTimeout(() => {
                setDisplayChildren(children);
            }, SlashTiming * 1.1);
        } else {
            setDisplayChildren(children);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [shouldDelay, children]);

    return shouldDelay ? displayChildren : children;
};

const ScrollRestore = ({ videoModalOpen, children }) => {
    const previousVideoModalOpen = usePrevious(videoModalOpen);

    // Restore scroll pos after closing video modal
    useEffect(() => {
        let scrollRaf = null;
        if (previousVideoModalOpen && !videoModalOpen) {
            try {
                const value = JSON.parse(
                    window.sessionStorage.getItem(`__next_scroll_${window.history?.state?.idx}`)
                );

                if (value?.y) {
                    scrollRaf = requestAnimationFrame(() => {
                        window.scrollTo(value.x, value.y);
                    });
                }
            } catch (e) {
                console.log('error restoring scroll pos', e); // eslint-disable-line
            }
        }

        return () => {
            if (scrollRaf) {
                cancelAnimationFrame(scrollRaf);
            }
        };
    }, [previousVideoModalOpen, videoModalOpen]);

    return children;
};

const App = ({ Component, pageProps, initialStore, reduxStore }: Props) => {
    const store = useClientStore(fromJS(initialStore), reduxStore);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Enable this again when ContinueWatchingMigration is removed
            loadPersistentActions(store.dispatch);
            store.dispatch(fetchParentalLock());
        }
    }, []); // eslint-disable-line

    const router = useRouter();

    useReloadManager();

    const videoModalOpen = Boolean(router.query.modalId);

    const RenderComponent = videoModalOpen ? React.Fragment : Component;

    return (
        <CookieConsentContextProvider version={COOKIE_CONSENT_VERSION}>
            <BaseHtmlHeader store={store} />
            <Provider store={store}>
                <UserContextProvider>
                    <BaseLayout>
                        <DelayedUnmount shouldDelay={router.query.delayUnmount}>
                            <ScrollRestore videoModalOpen={videoModalOpen}>
                                <RenderComponent {...pageProps} />
                            </ScrollRestore>
                        </DelayedUnmount>
                        {videoModalOpen && <VideoModal id={String(router.query.modalId)} />}
                    </BaseLayout>
                    <ExternalScripts />
                    <CookieConsentContainer />
                </UserContextProvider>
            </Provider>
        </CookieConsentContextProvider>
    );
};

App.getInitialProps = async (ctx: NextUrqlAppContext) => {
    const store = createServerStore(undefined);
    const appProps = await NextApp.getInitialProps(ctx);

    const req = ctx.ctx.req as any; // needed due to reduxStore
    if (req) {
        try {
            await setupAbiskoServer(store, req);
        } catch (e) {
            errorLogger('error abisko', e);
        }

        req.reduxStore = store;
    }

    return {
        ...appProps,
        initialStore: store.getState().toJS(),
        reduxStore: store,
    };
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
    reportWebVitalsToHelix(metric);
}

export default withUrqlClient(withUrqlSsr as NextUrqlClientConfig)(App as any); // needed due to reduxStore
